import useBulkActionBooks from "api/BookResource/useBulkActionBooks";
import { Sidebar } from "primereact/sidebar";
import { Tooltip } from "primereact/tooltip";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { BiBookAlt } from "react-icons/bi";
import { BsStarFill } from "react-icons/bs";
import { CgToggleOff, CgToggleOn } from "react-icons/cg";
import { FiAlertTriangle, FiX } from "react-icons/fi";
import { HiOutlineGift } from "react-icons/hi";
import { IoTrashOutline } from "react-icons/io5";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";

const BookBulkActions = Object.freeze({
	Activate: "activate",
	Deactivate: "deactivate",
	SetAsFree: "setAsFree",
	SetAsPremium: "setAsPremium",
	Delete: "deletePermanently",
});

/**
 * @typedef {Object} BulkActionsProps
 * @property {string[]|int[]} books
 * @property {()=>void} resetBooks
 * @param {BulkActionsProps} props
 */

const BulkActions = (props) => {
	const { books, resetBooks } = props;

	const countBooks = books?.length || 0;

	// Derived visibility state - no useEffect needed
	const visible = countBooks > 0;

	// State for tracking active action and delete confirmation
	const [activeAction, setActiveAction] = useState(null);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const hasRoles = useAuthStore((state) => state.hasRoles);

	// Memoize role check to avoid recalculating on every render
	const isAdminOrSuper = useMemo(() => hasRoles([Role.SuperAdmin, Role.Admin]), [hasRoles]);

	const { mutateAsync } = useBulkActionBooks();

	// Memoized submit handler with action tracking
	const handleSubmit = useCallback(
		async (action) => {
			setActiveAction(action);
			try {
				await mutateAsync({ books, action });
				resetBooks();
			} finally {
				setActiveAction(null);
				setShowDeleteConfirm(false);
			}
		},
		[books, mutateAsync, resetBooks],
	);

	// Memoized hide handler
	const handleHide = useCallback(() => {
		resetBooks();
		setShowDeleteConfirm(false);
	}, [resetBooks]);

	// Handle delete click - show confirmation
	const handleDeleteClick = useCallback(() => {
		setShowDeleteConfirm(true);
	}, []);

	// Handle delete confirmation cancel
	const handleCancelDelete = useCallback(() => {
		setShowDeleteConfirm(false);
	}, []);

	// Handle confirmed delete
	const handleConfirmDelete = useCallback(() => {
		handleSubmit(BookBulkActions.Delete);
	}, [handleSubmit]);

	// Keyboard support - Escape to close
	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === "Escape" && visible) {
				if (showDeleteConfirm) {
					setShowDeleteConfirm(false);
				} else {
					handleHide();
				}
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [visible, showDeleteConfirm, handleHide]);

	// Helper to check if a specific action is loading
	const isActionLoading = useCallback((action) => activeAction === action, [activeAction]);

	// Helper to render button content with loading state
	const renderButtonContent = (action, icon, size = 20) => {
		if (isActionLoading(action)) {
			return <AiOutlineLoading3Quarters size={size} className="animate-spin" />;
		}
		return icon;
	};

	return (
		<Sidebar
			visible={visible}
			position="bottom"
			onHide={handleHide}
			showCloseIcon={false}
			showHeader={false}
			className="bottom-8 bg-white border border-gray-200 shadow-lg rounded-full overflow-hidden font-sans "
			style={{
				width: "min(820px, 95vw)",
				left: "50%",
				height: "auto",
				minHeight: "50px",
				transform: "translateX(-50%)",
			}}
			aria-label="Bulk actions panel"
		>
			{/* Delete Confirmation Dialog */}
			{showDeleteConfirm && (
				<div className="absolute inset-0 bg-white bg-opacity-95 backdrop-blur-sm rounded-full flex items-center justify-center z-10 animate-in fade-in duration-200">
					<div className="flex items-center gap-4 px-6">
						<div className="bg-red-100 text-red-500 w-12 h-12 rounded-full flex items-center justify-center">
							<FiAlertTriangle size={24} />
						</div>
						<div className="flex flex-col">
							<span className="text-gray-900 font-bold text-base">
								Delete {countBooks} {countBooks === 1 ? "book" : "books"}?
							</span>
							<span className="text-gray-500 text-sm">This action cannot be undone</span>
						</div>
						<div className="flex items-center gap-2 ml-4">
							<button
								onClick={handleCancelDelete}
								type="button"
								className="px-4 py-2 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all text-sm font-semibold"
								aria-label="Cancel delete"
							>
								Cancel
							</button>
							<button
								onClick={handleConfirmDelete}
								disabled={activeAction !== null}
								type="button"
								className="px-4 py-2 rounded-full bg-red-500 hover:bg-red-600 text-white transition-all text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
								aria-label={`Confirm delete ${countBooks} books`}
								aria-busy={isActionLoading(BookBulkActions.Delete)}
							>
								{isActionLoading(BookBulkActions.Delete) ? (
									<AiOutlineLoading3Quarters size={16} className="animate-spin" />
								) : (
									<IoTrashOutline size={16} />
								)}
								Delete
							</button>
						</div>
					</div>
				</div>
			)}

			<div className="flex items-center justify-between gap-4 px-5 md:px-7 py-0 portal-bulk-bar">
				{/* Selection Info */}
				<div className="flex items-center gap-3 flex-shrink-0" aria-live="polite">
					<div className="bg-akgreener text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg shadow-akgreener/20">
						<BiBookAlt size={22} />
					</div>
					<div className="flex flex-col whitespace-nowrap">
						<span className="text-gray-900 font-black text-base tracking-tight leading-none">
							{countBooks} {countBooks === 1 ? "Book" : "Books"}
						</span>
						<span className="text-akgreener mt-0.5 text-sm">Selected for Action</span>
					</div>
				</div>

				{/* Actions Group */}
				<div className="flex items-center gap-1.5 flex-shrink-0">
					<div
						className="flex items-center bg-gray-100 rounded-full p-1.5 border border-gray-200 gap-1"
						role="toolbar"
						aria-label="Bulk actions"
					>
						<Tooltip target=".bulk-btn" position="top" className="text-xs" />

						{/* Pricing Toggles */}
						<div className="flex items-center gap-1" role="group" aria-label="Pricing actions">
							<button
								onClick={() => handleSubmit(BookBulkActions.SetAsFree)}
								disabled={activeAction !== null}
								className="bulk-btn group h-9 w-9 lg:w-auto lg:px-3 lg:gap-1.5 rounded-full flex items-center justify-center hover:bg-akgreener text-gray-500 hover:text-white transition-all active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed"
								data-pr-tooltip="Set as Free"
								aria-label={`Set ${countBooks} books as free`}
								aria-busy={isActionLoading(BookBulkActions.SetAsFree)}
							>
								{renderButtonContent(BookBulkActions.SetAsFree, <HiOutlineGift size={18} />)}
								<span className="hidden lg:inline text-xs font-medium">Free</span>
							</button>

							<button
								onClick={() => handleSubmit(BookBulkActions.SetAsPremium)}
								disabled={activeAction !== null}
								className="bulk-btn group h-9 w-9 lg:w-auto lg:px-3 lg:gap-1.5 rounded-full flex items-center justify-center hover:bg-akgreener text-gray-500 hover:text-white transition-all active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed"
								data-pr-tooltip="Set as Premium"
								aria-label={`Set ${countBooks} books as premium`}
								aria-busy={isActionLoading(BookBulkActions.SetAsPremium)}
							>
								{renderButtonContent(BookBulkActions.SetAsPremium, <BsStarFill size={18} />)}
								<span className="hidden lg:inline text-xs font-medium">Premium</span>
							</button>
						</div>

						{isAdminOrSuper && (
							<>
								<div className="w-px h-6 bg-gray-300 mx-1" aria-hidden="true" />

								{/* Activation Toggles */}
								<div
									className="flex items-center gap-1"
									role="group"
									aria-label="Activation actions"
								>
									<button
										onClick={() => handleSubmit(BookBulkActions.Activate)}
										disabled={activeAction !== null}
										className="bulk-btn group h-9 w-9 lg:w-auto lg:px-3 lg:gap-1.5 rounded-full flex items-center justify-center hover:bg-blue-500 text-gray-500 hover:text-white transition-all active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed"
										data-pr-tooltip="Activate All"
										aria-label={`Activate ${countBooks} books`}
										aria-busy={isActionLoading(BookBulkActions.Activate)}
									>
										{renderButtonContent(BookBulkActions.Activate, <CgToggleOn size={18} />)}
										<span className="hidden lg:inline text-xs font-medium">Activate</span>
									</button>

									<button
										onClick={() => handleSubmit(BookBulkActions.Deactivate)}
										disabled={activeAction !== null}
										className="bulk-btn group h-9 w-9 lg:w-auto lg:px-3 lg:gap-1.5 rounded-full flex items-center justify-center hover:bg-blue-500 text-gray-500 hover:text-white transition-all active:scale-90 disabled:opacity-50 disabled:cursor-not-allowed"
										data-pr-tooltip="Deactivate All"
										aria-label={`Deactivate ${countBooks} books`}
										aria-busy={isActionLoading(BookBulkActions.Deactivate)}
									>
										{renderButtonContent(BookBulkActions.Deactivate, <CgToggleOff size={18} />)}
										<span className="hidden lg:inline text-xs font-medium">Deactivate</span>
									</button>
								</div>
							</>
						)}

						<div className="w-px h-6 bg-gray-300 mx-1" aria-hidden="true" />

						{/* Danger Action - Visually distinct */}
						<button
							onClick={handleDeleteClick}
							disabled={activeAction !== null}
							className="bulk-btn group w-9 h-9 rounded-full flex items-center justify-center bg-red-50 hover:bg-red-500 text-red-500 hover:text-white transition-all active:scale-90 border border-red-200 hover:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
							data-pr-tooltip="Delete Selected"
							aria-label={`Delete ${countBooks} selected books`}
						>
							<IoTrashOutline size={18} />
						</button>
					</div>

					{/* Close/Cancel Button */}
					<button
						onClick={handleHide}
						type="button"
						className="group flex items-center gap-1 px-2 py-2 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all text-sm font-bold active:scale-95 flex-shrink-0"
						aria-label="Cancel selection and close"
					>
						<FiX size={20} className="group-hover:rotate-90 transition-transform duration-300" />
					</button>
				</div>
			</div>
		</Sidebar>
	);
};

export default BulkActions;
